/**
 * FHIR persistence for healthcare service entitlement (user allowlist).
 */
import type { MedplumClient } from '@medplum/core';
import type { List, Organization, Practitioner, PractitionerRole, Reference } from '@medplum/fhirtypes';
import {
  PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM,
  LIST_TYPE_SYSTEM,
  ENABLED_HEALTHCARE_SERVICES_LIST_CODE,
} from '@unify/fhir-model/constants';
import {
  buildPracticeEnabledServicesList,
  getEnabledHealthcareServiceIdsFromList,
  getProviderHealthcareServiceIds,
  healthcareServiceReference,
  isPracticeEnabledServicesList,
  setProviderHealthcareServiceIds,
} from '@unify/fhir-model/resources/healthcare-service';
import { getPractitionerOrganizationRef, getRole } from '@unify/fhir-model/resources/practitioner';
import { PRACTITIONER_ROLE_SYSTEM } from '@unify/fhir-model/constants';

export async function fetchPracticeEnabledServicesList(
  medplum: MedplumClient,
  practiceOrgId: string,
): Promise<List | undefined> {
  const lists = await medplum.searchResources('List', {
    identifier: `${PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM}|${practiceOrgId}`,
    _count: '1',
  });
  return lists[0];
}

export async function fetchPracticeEnabledHealthcareServiceIds(
  medplum: MedplumClient,
  practiceOrgId: string,
): Promise<string[]> {
  const list = await fetchPracticeEnabledServicesList(medplum, practiceOrgId);
  if (!list) return [];
  return getEnabledHealthcareServiceIdsFromList(list);
}

export async function upsertPracticeEnabledHealthcareServiceIds(
  medplum: MedplumClient,
  practiceOrgId: string,
  serviceIds: string[],
): Promise<List> {
  const existing = await fetchPracticeEnabledServicesList(medplum, practiceOrgId);
  const list = buildPracticeEnabledServicesList(practiceOrgId, serviceIds, existing);
  if (existing?.id) {
    return medplum.updateResource(list);
  }
  return medplum.createResource(list);
}

export async function fetchProviderPractitionerRole(
  medplum: MedplumClient,
  practitionerId: string,
  practiceOrgId: string,
): Promise<PractitionerRole | undefined> {
  const roles = await medplum.searchResources('PractitionerRole', {
    practitioner: `Practitioner/${practitionerId}`,
    organization: `Organization/${practiceOrgId}`,
    _count: '10',
  });
  return roles.find((r) => r.active !== false) ?? roles[0];
}

export async function fetchProviderHealthcareServiceIds(
  medplum: MedplumClient,
  practitionerId: string,
  practiceOrgId: string,
): Promise<string[]> {
  const role = await fetchProviderPractitionerRole(medplum, practitionerId, practiceOrgId);
  if (!role) return [];
  return getProviderHealthcareServiceIds(role);
}

export async function updateProviderHealthcareServiceIds(
  medplum: MedplumClient,
  practitionerId: string,
  practiceOrgId: string,
  serviceIds: string[],
): Promise<PractitionerRole | undefined> {
  const role = await fetchProviderPractitionerRole(medplum, practitionerId, practiceOrgId);
  if (!role) return undefined;
  const updated = setProviderHealthcareServiceIds(role, serviceIds);
  return medplum.updateResource(updated);
}

/**
 * When a new platform catalog HealthcareService is created, enable it for every
 * practice List and authorize it on every PractitionerRole in those practices.
 * Keeps the calendar usable without manual practice/provider toggles.
 */
export async function cascadeNewCatalogServiceToPractices(
  medplum: MedplumClient,
  serviceId: string,
): Promise<void> {
  medplum.invalidateSearches('List');
  const lists = await medplum.searchResources('List', {
    code: `${LIST_TYPE_SYSTEM}|${ENABLED_HEALTHCARE_SERVICES_LIST_CODE}`,
    _count: '100',
  });

  const practiceOrgIds: string[] = [];
  for (const list of lists) {
    if (!isPracticeEnabledServicesList(list)) continue;
    const practiceOrgId = list.identifier?.find(
      (id) => id.system === PRACTICE_SERVICES_LIST_IDENTIFIER_SYSTEM,
    )?.value;
    if (!practiceOrgId) continue;

    const current = getEnabledHealthcareServiceIdsFromList(list);
    if (!current.includes(serviceId)) {
      await upsertPracticeEnabledHealthcareServiceIds(medplum, practiceOrgId, [...current, serviceId]);
    }
    practiceOrgIds.push(practiceOrgId);
  }

  for (const practiceOrgId of practiceOrgIds) {
    medplum.invalidateSearches('PractitionerRole');
    const roles = await medplum.searchResources('PractitionerRole', {
      organization: `Organization/${practiceOrgId}`,
      _count: '100',
    });
    for (const role of roles) {
      const current = getProviderHealthcareServiceIds(role);
      if (current.includes(serviceId)) continue;
      await medplum.updateResource(setProviderHealthcareServiceIds(role, [...current, serviceId]));
    }
  }
}

function practitionerIdFromRole(role: PractitionerRole): string | undefined {
  const ref = role.practitioner?.reference;
  if (!ref?.startsWith('Practitioner/')) return undefined;
  return ref.slice('Practitioner/'.length);
}

/** Active PractitionerRoles for a practitioner (expects at most one in production). */
export async function fetchActivePractitionerRolesByPractitioner(
  medplum: MedplumClient,
  practitionerId: string,
): Promise<PractitionerRole[]> {
  const roles = await medplum.searchResources('PractitionerRole', {
    practitioner: `Practitioner/${practitionerId}`,
    _count: '20',
  });
  return roles.filter((role) => role.active !== false);
}

const ROLE_PAGE_SIZE = 100;

/**
 * Practitioner ids whose active PractitionerRole.healthcareService includes the given
 * service (full-table scan, fully paginated). Inactive roles are ignored so a stale
 * historical role never reports a user as having access.
 */
async function scanPractitionersWithService(
  medplum: MedplumClient,
  serviceId: string,
): Promise<string[]> {
  const ids: string[] = [];
  let offset = 0;
  let page: PractitionerRole[];
  do {
    page = await medplum.searchResources('PractitionerRole', {
      _count: String(ROLE_PAGE_SIZE),
      _offset: String(offset),
    });
    for (const role of page) {
      if (role.active === false) continue;
      const practitionerId = practitionerIdFromRole(role);
      if (!practitionerId) continue;
      if (getProviderHealthcareServiceIds(role).includes(serviceId)) {
        ids.push(practitionerId);
      }
    }
    offset += ROLE_PAGE_SIZE;
  } while (page.length === ROLE_PAGE_SIZE);
  return [...new Set(ids)];
}

/**
 * Practitioner ids whose active PractitionerRole.healthcareService includes the given
 * service. Only active roles count, matching what `toggleAppointmentTypeAccess` mutates.
 */
export async function fetchPractitionersWithService(
  medplum: MedplumClient,
  serviceId: string,
): Promise<string[]> {
  try {
    const ids: string[] = [];
    let offset = 0;
    let page: PractitionerRole[];
    do {
      page = await medplum.searchResources('PractitionerRole', {
        service: `HealthcareService/${serviceId}`,
        _count: String(ROLE_PAGE_SIZE),
        _offset: String(offset),
      });
      for (const role of page) {
        if (role.active === false) continue;
        const practitionerId = practitionerIdFromRole(role);
        if (practitionerId) ids.push(practitionerId);
      }
      offset += ROLE_PAGE_SIZE;
    } while (page.length === ROLE_PAGE_SIZE);
    const unique = [...new Set(ids)];
    // Medplum may accept an unsupported `service` param but return an empty set; scan to verify.
    if (unique.length > 0) {
      return unique;
    }
  } catch {
    // fall through to scan
  }
  return scanPractitionersWithService(medplum, serviceId);
}

/**
 * All active staff Practitioners, fully paginated.
 *
 * Clients are `Patient` resources, so querying `Practitioner` naturally excludes them and
 * includes every other user type (unify-admin, unify-assistant, practice-owner/admin,
 * supervisor, provider, …). A user may not yet have a `PractitionerRole`; one is created on
 * demand when access is first granted — see `toggleAppointmentTypeAccess`.
 */
export async function fetchAssignablePractitioners(medplum: MedplumClient): Promise<Practitioner[]> {
  const PAGE_SIZE = 100;
  const result: Practitioner[] = [];
  let offset = 0;
  let page: Practitioner[];
  do {
    page = await medplum.searchResources('Practitioner', {
      _count: String(PAGE_SIZE),
      _offset: String(offset),
      _sort: 'name',
    });
    result.push(...page);
    offset += PAGE_SIZE;
  } while (page.length === PAGE_SIZE);
  return result.filter((p) => Boolean(p.id) && p.active !== false);
}

/**
 * Canonical `PractitionerRole.code[]` derived from the user's role slug, matching the
 * coding the invite/role-assignment bot writes so access-created roles reconcile cleanly.
 */
function practitionerRoleCode(practitioner: Practitioner): PractitionerRole['code'] | undefined {
  const slug = getRole(practitioner);
  if (!slug) return undefined;
  const display = slug
    .split('-')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
  return [{ coding: [{ system: PRACTITIONER_ROLE_SYSTEM, code: slug, display }] }];
}

/**
 * Create a PractitionerRole carrying the given service for a user who does not yet have
 * one. Scoped to the user's practice via the Practitioner org extension, and stamped with
 * the canonical role `code` so the invite/role-assignment bot reconciles (rather than
 * forking) it later. Throws when the user has no org so we never create an orphan role.
 */
async function createPractitionerRoleWithService(
  medplum: MedplumClient,
  practitionerId: string,
  serviceId: string,
): Promise<PractitionerRole> {
  const practitioner = await medplum.readResource('Practitioner', practitionerId);
  const orgRef = getPractitionerOrganizationRef(practitioner);
  if (!orgRef) {
    throw new Error('User has no practice assigned; cannot grant appointment type access.');
  }
  const code = practitionerRoleCode(practitioner);
  const role: PractitionerRole = {
    resourceType: 'PractitionerRole',
    active: true,
    practitioner: { reference: `Practitioner/${practitionerId}` },
    organization: orgRef as Reference<Organization>,
    ...(code ? { code } : {}),
    healthcareService: [healthcareServiceReference(serviceId)],
  };
  return medplum.createResource(role);
}

/**
 * Grant or revoke a platform-catalog HealthcareService on a user's allowlist.
 *
 * - No active role: creates one (scoped to the user's org, with canonical role code)
 *   carrying the service; throws if the user has no org.
 * - Exactly one active role: toggles the service in `healthcareService`.
 * - Multiple active roles: throws, so we never mutate the wrong role.
 */
export async function toggleAppointmentTypeAccess(
  medplum: MedplumClient,
  practitionerId: string,
  serviceId: string,
): Promise<PractitionerRole> {
  const activeRoles = await fetchActivePractitionerRolesByPractitioner(medplum, practitionerId);
  if (activeRoles.length > 1) {
    throw new Error(
      'Practitioner has multiple active roles; cannot safely update healthcare service access.',
    );
  }

  if (activeRoles.length === 0) {
    const created = await createPractitionerRoleWithService(medplum, practitionerId, serviceId);
    medplum.invalidateSearches('PractitionerRole');
    return created;
  }

  const role = activeRoles[0];
  const current = new Set(getProviderHealthcareServiceIds(role));
  if (current.has(serviceId)) {
    current.delete(serviceId);
  } else {
    current.add(serviceId);
  }

  const updated = setProviderHealthcareServiceIds(role, [...current]);
  const saved = await medplum.updateResource(updated);
  medplum.invalidateSearches('PractitionerRole');
  return saved;
}
